/**
 * @fileOverview Analytics – link and error tracking
 * @name Analytics Widget
 * @author dquock, jamuferguson
 *
 * This file is a wrapper around site catalyst and fpti
 * and allows us to easily fire off tracking calls to
 * both at the same time.
 *
 * Primarily referenced in our global page handler "global.es6"
 */

import * as logger from '../verbose-logger';

// store for later
const noop = () => {};

// safely shadow these globals
let s = window.s || { t: noop, tl: noop };
let PAYPAL = window.PAYPAL;

// NOTE: it's not safe to shadow window.fpti at the module level
// because it gets reset after 500ms in pa.js (see: pageTracking.dust)

// instantiate the view (with a backup for mocks mode)
let emptyAnalytics = { recordClick: noop, recordImpression: noop };
let analytics = PAYPAL && PAYPAL.analytics ? new PAYPAL.analytics.Analytics() : emptyAnalytics;

// Start client side error tracking
PAYPAL &&
PAYPAL.analytics &&
PAYPAL.analytics.startClientErrorTracking && PAYPAL.analytics.startClientErrorTracking();

// can access FPTI performance info from PAYPAL.analytics.perf
// https://dev.paypal.com/wiki/General/FPTI-PerformanceTracking

/*************
 * Public API
 *************/

/**
 * Simple back button tracking
 */
export function trackBackButton() {
    trackAction('back');
}

/**
 * Adding new data - pxp data
 */
export function addPxpTags(data = {}) {
  try {
    Object.assign(window.fpti, data);
  }
  catch (exception) {
    logger.log('Analytis Exception Found:', exception);
  }
}

/**
 * Wrapper around trackLink that lets us send additional FPTI keys
 * @param linkName - the link name for the trackLink call
 * @param pageGroup - the page group for the trackLink call
 * @param {String} pageName - The actual page name (prev. pageName2)
 * @param data - key value pairs we want to send to FPTI then remove
 */
export function trackLinkWithData(linkName, pageGroup, pageName, data, trackType) {
    let fpti = window.fpti || {};

    // apply extra fields to window.fpti
    Object.assign(fpti, data);

    trackLink(linkName, pageGroup, pageName, trackType);

     // remove those additional keys
    Object.keys(data).forEach((key) => {
        delete fpti[key];
    });

}

/**
 * Set FPTI/SC variables and fire page tracking beacons
 *
 * @param {String} pageGroup - The page group (prev. pageName)
 * @param {String} pageName - The actual page name (prev. pageName2)
 */
export function trackPage(pageGroup, pageName, custKey, custValue) {

    let fpti = {
        data:{}
    };

    logger.log('Recording analytics:', [pageGroup, pageName, custKey, custValue]);

    // set pageNames
    fpti.data.page = s.prop25 = s.eVar25 = pageName;
    fpti.data.pgrp = s.pageName = pageGroup;
	fpti.data.tmpl = pageName;

	if ( custKey && custValue){
		fpti.data[custKey] = custValue;
	}

    // clear out error props, in case they exist
    fpti.data.erpg = s.prop14 = undefined; // error message
    fpti.data.erfd = s.prop15 = undefined; // form field with error (field1|field2|field3)
    fpti.data.eccd = s.prop29 = undefined; // error code
    fpti.data.version = 'fixathon';
    fpti.data.tsrce = 'donatenodeweb';

    try {
		fireBeacon(fpti);
    } catch (exception) {
        logger.log('Analytis Exception Found:', exception);
    }
}

/**
 * Set FPTI/SC variables and fire page tracking beacons with data
 *
 * @param {String} pageGroup - The page group (prev. pageName)
 * @param {String} pageName - The actual page name (prev. pageName2)
 */
export function trackPageWithData(pageGroup, pageName, data = {}) {

    let fpti = {
        data: {}
    };

    logger.log('Recording analytics:', [pageGroup, pageName, data]);
    
    // set pageNames
    fpti.data.page = s.prop25 = s.eVar25 = pageName;
    fpti.data.pgrp = s.pageName = pageGroup;
    fpti.data.tmpl = pageName;
    
    Object.assign(fpti.data, data);

    // clear out error props, in case they exist
    fpti.data.erpg = s.prop14 = undefined; // error message
    fpti.data.erfd = s.prop15 = undefined; // form field with error (field1|field2|field3)
    fpti.data.eccd = s.prop29 = undefined; // error code
    fpti.data.version = 'fixathon';
    fpti.data.tsrce = 'donatenodeweb';

    try {
		fireBeacon(fpti);
    } catch (exception) {
        logger.log('Analytis Exception Found:', exception);
    }
}


/**
 * Set FPTI/SC variables and fire tracking beacons
 *
 * @param {String} linkName - The name of the link
 * @param {String} pageGroup - The page group (prev. pageName)
 * @param {String} pageName - The actual page name (prev. pageName2)
 * @param {String} trackType - i.e "link"
 * @param {String} transactionDetailsLinks
 */
export function trackLink(linkName, pageGroup, pageName, trackType, transactionDetailsLinks) {

    let fpti = {
        data: {}
    };

    logger.log('Recording analytics:', [linkName, pageGroup, pageName, trackType, transactionDetailsLinks]);

    // set linkname
    fpti.data.link = s.prop26 = linkName;

    // set pageNames
    fpti.data.page = s.prop25 = s.eVar25 = pageName;
    fpti.data.pglk = s.prop27 = `${pageGroup}|${linkName}`;
    fpti.data.pgln = `${pageName}|${linkName}`;
    fpti.data.pgrp = s.pageName = pageGroup;

    // clear out error props, in case they exist
    fpti.data.erpg = s.prop14 = undefined; // error message
    fpti.data.erfd = s.prop15 = undefined; // form field with error (field1|field2|field3)
    fpti.data.eccd = s.prop29 = undefined; // error code
    fpti.data.version = 'fixathon';
    fpti.data.tsrce = 'donatenodeweb';

    if (transactionDetailsLinks) {
        // prop28 is the sc var for transaction details links when
        // selecting a transaction (link1|link2|link3)
        s.prop28 = transactionDetailsLinks;
    }

    try {
        switch (trackType) {
            case 'link':
                fireBeacon(fpti, s.prop26, true);
                break;

            default:
                fireBeacon(fpti);
                break;
        }
    } catch (exception) {
        logger.log('Analytis Exception Found:', exception);
    }

    // set attrs for automation
    // $('#analytics').attr('data-pagename', pageName)
    //	.attr('data-c25', pageName2)
    //	.attr('data-c27', s.prop27)
    //	.attr('data-c28', s.prop28)
    //	.attr('data-v56', s.eVar56);

}

/**
 * Link tracking
 * @param {Object} event - click event || the clicked jQuery object of the input used to submit a form
 * if event isn't defined or is null, explicitly specify params
 * @param {String} linkName - name attribute from the link
 * @param {String} pageGroup - data-pagename the link is going to
 * @param {String} pageName - data-pagename2 the link is going to
 * @param {String} trackType - page-level or not
 * @param {String} transactionDetailsLinks - activity transaction details links
 */
export function trackElement($element) {
    let linkName = $element.attr('name') || $element && $element.html();
    let pageGroup = $element.data('pagename') || s.pageName;
    let trackType = $element.data('track-type') || 'link';
    let pageName = $element.data('pagename2') || `${pageGroup}:::${trackType}`;
    let transactionDetailsLinks = $element.data('transactiondetailslinks');
    trackLink(linkName, pageGroup, pageName, trackType, transactionDetailsLinks);
}

/**
 *	Tracks errors
 *	@param {Object} error - The object containing the props to set
 */
export function trackError (error) { // eslint-disable-line consumerweb/param-names

    let fpti = {
        data:{}
    };

    logger.log('Recording analytics:', error);

    // set error props
    fpti.data.erpg = s.prop14 = error.message; // error message
    fpti.data.erfd = s.prop15 = error.fieldId; // form field with error (field1|field2|field3)
    fpti.data.eccd = s.prop29 = error.code; // error code
    fpti.data.tsrce = 'donatenodeweb';

    fireBeacon(fpti);

    // set attrs for automation
    // $('#analytics').attr('data-pagename', s.pageName)
    //	.attr('data-c25', s.prop25)
    //	.attr('data-c27', s.prop27);
}

/**
 *	Tracks link on a page
 *	keeps the pagenames the same, but tracks the link
 *	@param {String} linkName linkName to track
 */
export function trackAction (linkName) {

    let fpti = {
        data:{}
    };

    logger.log('Recording analytics:', linkName);

    // set linkname
    fpti.data.link = s.prop26 = linkName;
    fpti.data.pglk = s.prop27 = `${s.pageName}|${linkName}`;

    // clear out error props, in case they exist
    fpti.data.erpg = s.prop14 = undefined; // error message
    fpti.data.erfd = s.prop15 = undefined; // form field with error (field1|field2|field3)
    fpti.data.eccd = s.prop29 = undefined; // error code
    fpti.data.tsrce = 'donatenodeweb';

    fireBeacon(fpti, s.prop26, true);

    // set attr for automation
    // $('#analytics').attr('data-c27', s.prop27);
}

/**************
 * Private API
 **************/

/**
 * Set FPTI/SC variables and fire tracking beacons
 * @param {String} prop - Prop value to record
 * @param {Boolean} isLink - whether to record it as an link click or a page transition
 */
function fireBeacon(fpti={}, prop, isLink) { 
    if (isLink && arguments.length > 1) {
        s.tl(true, 'o', prop); // micro/link tracking
        analytics.recordClick(fpti); // fpti beacon
    } else {
        s.t(); // fire page-level link tracking event
        analytics.recordImpression(fpti); // fpti beacon
    }
}



// WEBPACK FOOTER //
// ./vendor/analytics/index.js